import React from 'react'
import { useLocation } from 'react-router-dom'

import { ButtonSize } from '../ui/button'
import useScreenSize from '../../context/screenSize'
import { isFeedbackBannerExcludedRoute } from '../../lib/feedbackBannerRoutes'
import { isFeedbackBannerSnoozed, snoozeFeedbackBanner } from '../../lib/feedbackStorage'
import { ReactComponent as ChatIcon } from '../../static/images/feedback-chat.svg'
import { ReactComponent as CloseIcon } from '../../static/images/close.svg'
import FeedbackButton from './FeedbackButton'
import './styles.scss'

const FEEDBACK_EMAIL = 'info@tinyinvoice.app'
const BANNER_BOTTOM_GAP_PX = 24 // matches bottom: 1.5rem

const FeedbackBanner: React.FC = () => {
  const { pathname } = useLocation()
  const { isSidebarCollapsed } = useScreenSize()
  const [visible, setVisible] = React.useState(() => !isFeedbackBannerSnoozed())
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(
    () => typeof document !== 'undefined' && document.body.classList.contains('mobile-sidebar-open'),
  )
  const bannerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const syncMobileSidebarOpen = () => {
      setMobileSidebarOpen(document.body.classList.contains('mobile-sidebar-open'))
    }
    const observer = new MutationObserver(syncMobileSidebarOpen)
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const bannerActive = visible
    && !isFeedbackBannerExcludedRoute(pathname)
    && !(isSidebarCollapsed && mobileSidebarOpen)

  React.useLayoutEffect(() => {
    if (!bannerActive) {
      document.body.classList.remove('feedback-banner-visible')
      document.body.style.removeProperty('--feedback-banner-offset')
      return
    }

    document.body.classList.add('feedback-banner-visible')

    const updateOffset = () => {
      const banner = bannerRef.current
      if (!banner) return
      const reserve = banner.getBoundingClientRect().height + BANNER_BOTTOM_GAP_PX
      document.body.style.setProperty('--feedback-banner-offset', `${reserve}px`)
    }

    updateOffset()
    const observer = new ResizeObserver(updateOffset)
    if (bannerRef.current) observer.observe(bannerRef.current)
    window.addEventListener('resize', updateOffset)

    return () => {
      document.body.classList.remove('feedback-banner-visible')
      document.body.style.removeProperty('--feedback-banner-offset')
      observer.disconnect()
      window.removeEventListener('resize', updateOffset)
    }
  }, [bannerActive])

  const handleClose = () => {
    snoozeFeedbackBanner()
    setVisible(false)
  }

  const handleSubmitted = () => {
    setVisible(false)
  }

  if (!bannerActive) return null

  return (
    <>
      <div ref={bannerRef} className='feedback-banner' role='region' aria-label='Feedback'>
        <button
          type='button'
          className='feedback-banner__close'
          onClick={handleClose}
          aria-label='Close feedback banner'
        >
          <CloseIcon width={20} height={20} />
        </button>
        <div className='feedback-banner__icon' aria-hidden>
          <ChatIcon width={24} height={24} />
        </div>
        <div className='feedback-banner__content'>
          <p className='feedback-banner__question'>What would make TinyInvoice.app even better for you?</p>
          <small className='feedback-banner__email'>{FEEDBACK_EMAIL}</small>
        </div>
        <FeedbackButton
          size={ButtonSize.SMALL}
          className='feedback-banner__cta'
          onSubmitted={handleSubmitted}
        >
          Let me know!
        </FeedbackButton>
      </div>
    </>
  )
}

export default FeedbackBanner
